import React from "react";

import { createRoot, hydrateRoot } from "react-dom/client";
import {
    loadQuery,
    PreloadedQuery,
    RelayEnvironmentProvider
} from "react-relay";
import {
    GraphQLTaggedNode,
    OperationType,
    PreloadableConcreteRequest,
    VariablesOf
} from "relay-runtime";
import { v4 as uuidv4 } from "uuid";

import { initUFO } from "@/analytics/react-ufo/initUFO";
import { getAnalyticsWebClient } from "@/analytics/wac-analytics/get-analytics-web-client";
import {
    IntercomProperties,
    IntercomSettingsProperties
} from "@/libraries/intercom/src";
import { RELAY_API_CONTENTFUL } from "@/utils/constant";
import { CONTENTFUL_GRAPHQL_API_URL } from "@/utils/urls.ts";

import { type AppPricingData } from "./contentful";
import { initializeFeatureGatesClient } from "./feature-gates/initialization";
import { Layout } from "./layout";
import {
    AvailableProductsResponse,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "./libraries/bxp-web-stargate-client/src";
import { PageWrapper } from "./page-wrapper";
import { initRelayEnvironment, RecordMap } from "./relay-environment";
import {
    overrideAjsAnonIdFromCookie,
    overrideAtlassianAccountIdFromCookie,
    overrideCustomAttributes,
    edgeCachingPaths
} from "./utils/fg-bootstrap-values-override-utils";
import { GridOverlayFunction } from "./utils/grid-overlay";
import { isPerformanceOnetrustEnabled } from "./utils/onetrust-provider";
import { SentryIntlProvider } from "./utils/sentry-intl";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

window.BUILD_KEY = process.env["VERSION"];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

declare global {
    interface Window {
        IS_STATIC?: boolean;
        BUILD_KEY?: string;
        entryName?: string;
        SSR_languagePack: Record<string, string>;
        prices?: AppPricingData;
        availableProducts?: AvailableProductsResponse;
        userProfile?: UserProfile;
        userProfileStatus?: ServerSideRequestStatus;
        userContext?: UserProfileResponse;
        userContextStatus?: ServerSideRequestStatus;
        keyword?: string;
        subAnchor?: string;
        featureFlags: Record<string, FeatureFlagType>;
        cmsPageVariant?: string;
        mlTemplateIds?: string[];
        countryCode?: UserLocale;
        Intercom?: IntercomProperties;
        intercomSettings?: IntercomSettingsProperties;
        toggleGrid?: GridOverlayFunction;
        relayStoreSnapshot?: RecordMap;
        relayQueryRef?: PreloadedQuery<OperationType>;
        CONTENTFUL_BASE_URL_GQL: string;
        CONTENTFUL_SPACE_ID_PRODUCT_TEMPLATES: string;
        CONTENTFUL_ENVIRONMENT_PRODUCT_TEMPLATES: string;
        CONTENTFUL_DELIVERY_KEY_PRODUCT_TEMPLATES: string;
    }
}

export const init = <TQuery extends OperationType>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component: React.ComponentType<any>,
    query: GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>,
    queryVars: VariablesOf<TQuery>,
    relayApi?: typeof RELAY_API_CONTENTFUL | undefined
) => {
    const analyticsWebClient = getAnalyticsWebClient();
    const { locale, path } = parseRoute(window.location);
    isPerformanceOnetrustEnabled() && initUFO(analyticsWebClient, path);

    if (edgeCachingPaths.includes(window.location.pathname)) {
        // Override for Edge Caching
        overrideAjsAnonIdFromCookie();
        overrideAtlassianAccountIdFromCookie();
        overrideCustomAttributes();
    }

    initializeFeatureGatesClient(window.featureGateBootstrapValues).then(() => {
        const isStatic = typeof window !== "undefined" && window.IS_STATIC;

        const snapshot = window.relayStoreSnapshot;

        let relayBaseUrl = undefined;
        let relayAuthKey = undefined;
        if (relayApi === RELAY_API_CONTENTFUL) {
            relayBaseUrl = CONTENTFUL_GRAPHQL_API_URL;
            relayAuthKey = window.CONTENTFUL_DELIVERY_KEY_PRODUCT_TEMPLATES;
        }

        const relayEnvironment = initRelayEnvironment(
            snapshot,
            relayBaseUrl,
            relayAuthKey
        );
        const queryRef = loadQuery(relayEnvironment, query, queryVars);

        if (isStatic) {
            // @ts-ignore
            const root = createRoot(document.getElementById("wac"));
            root.render(
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <RelayEnvironmentProvider environment={relayEnvironment}>
                        <PageWrapper
                            analyticsWebClient={analyticsWebClient}
                            pureClientMode={true}
                            location={window.location}
                            pricing={window.prices}
                            availableProducts={window.availableProducts}
                            userProfile={window.userProfile}
                            userProfileStatus={window.userProfileStatus}
                            userContext={window.userContext}
                            userContextStatus={window.userContextStatus}
                            countryCode={window.countryCode}
                            mlTemplateIds={window.mlTemplateIds}
                            keyword={window.keyword}
                            subAnchor={window.subAnchor}
                            queryRef={queryRef}
                        >
                            {Component}
                        </PageWrapper>
                    </RelayEnvironmentProvider>
                </SentryIntlProvider>
            );
        } else {
            hydrateRoot(
                // @ts-ignore
                document.getElementById("wac-root"),
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <RelayEnvironmentProvider environment={relayEnvironment}>
                        <Layout
                            analyticsWebClient={analyticsWebClient}
                            location={window.location}
                            pricing={window.prices}
                            availableProducts={window.availableProducts}
                            userProfile={window.userProfile}
                            userProfileStatus={window.userProfileStatus}
                            userContext={window.userContext}
                            userContextStatus={window.userContextStatus}
                            countryCode={window.countryCode}
                            mlTemplateIds={window.mlTemplateIds}
                            keyword={window.keyword}
                            subAnchor={window.subAnchor}
                            queryRef={queryRef}
                        >
                            {Component}
                        </Layout>
                    </RelayEnvironmentProvider>
                </SentryIntlProvider>,
                {
                    onRecoverableError: (error, errorInfo) => {
                        if (process.env["CI"] === "true") {
                            document
                                .getElementById("wac-root")
                                ?.setAttribute("hydration-error", "true");
                        }
                        if (process.env["MODE"] === "dev") {
                            console.log(
                                `Recoverable Error (including Hydration): ${error}`,
                                errorInfo
                            );
                        }
                    }
                }
            );
        }
    });
};
